import { useState, useEffect, useRef } from 'react';
import { RefreshCcw, ChevronDown, Calendar } from 'lucide-react';
import { useDate } from '../contexts/DateContext';
import CalendarModal from './child/CalenderModal';

export default function HomeBreadcrumb() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Last 7 days");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  const [tempStart, setTempStart] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);

  const dropdownRef = useRef(null);

  const { startDate, endDate, setStartDate, setEndDate } = useDate();

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showYearSelector, setShowYearSelector] = useState(false);

  const isMobile = viewportWidth < 640;
  const options = ["Last 7 days", "Last 30 days", "Custom range"];
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getRangeFromNow = (days) => ({
    start: new Date(Date.now() - days * 24 * 60 * 60 * 1000),
    end: new Date(),
  });

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
    setIsCalendarOpen(false);
  };

  const toggleCalendar = () => {
    setIsCalendarOpen((prev) => !prev);
    setSelectingStartDate(true);
    setTempStart(null);
    setShowYearSelector(false);
    if (!isCalendarOpen) {
      const today = new Date();
      setCurrentMonth(today.getMonth());
      setCurrentYear(today.getFullYear());
    }
  };

  const selectOption = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
    if (option === "Custom range") {
      const today = new Date();
      setCurrentMonth(today.getMonth());
      setCurrentYear(today.getFullYear());
    } else {
      const { start, end } = getRangeFromNow(option === "Last 7 days" ? 7 : 30);
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleRefresh = () => {
    if (selectedOption === "Custom range") {
      setStartDate(startDate);
      setEndDate(endDate);
    } else {
      const { start, end } = getRangeFromNow(selectedOption === "Last 7 days" ? 7 : 30);
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleDateClick = (date) => {
    if (selectingStartDate) {
      setTempStart(date);
      setHoverDate(null);
      setSelectingStartDate(false);
    } else {
      if (tempStart && date >= tempStart) {
        applyDateRangeFromCalendar(tempStart, date);
      } else {
        applyDateRangeFromCalendar(date, tempStart);
      }
      setSelectingStartDate(true);
    }
  };

  const applyDateRangeFromCalendar = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setIsCalendarOpen(false);
  };

  const handleDateHover = (date) => {
    if (!selectingStartDate && tempStart) {
      setHoverDate(date);
    }
  };

  const cancelDateSelection = () => {
    setIsCalendarOpen(false);
  };

  const applyDateRange = () => {
    if (tempStart && hoverDate) {
      applyDateRangeFromCalendar(tempStart, hoverDate);
    } else if (tempStart) {
      applyDateRangeFromCalendar(tempStart, tempStart);
    }
  };

  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((prev) => prev - 1);
    } else {
      setCurrentMonth((prev) => prev - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((prev) => prev + 1);
    } else {
      setCurrentMonth((prev) => prev + 1);
    }
  };

  const toggleYearSelector = () => setShowYearSelector((prev) => !prev);
  const selectYear = (year) => {
    setCurrentYear(year);
    setShowYearSelector(false);
  };
  const selectMonth = (month) => setCurrentMonth(month);

  const generateYearOptions = () => {
    const years = [];
    const now = new Date().getFullYear();
    for (let i = now - 10; i <= now + 10; i++) years.push(i);
    return years;
  };

  const generateCalendarDates = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const calendarDays = [];
    let day = 1;

    for (let i = 0; i < 6; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < startingDay) || day > daysInMonth) {
          week.push(null);
        } else {
          week.push(new Date(currentYear, currentMonth, day++));
        }
      }
      calendarDays.push(week);
      if (day > daysInMonth) break;
    }

    return calendarDays;
  };

  const isSameDate = (a, b) => a?.toDateString() === b?.toDateString();

  const isSelectedDate = (date) => {
    if (!date) return false;
    return (
      isSameDate(date, tempStart) ||
      isSameDate(date, hoverDate) ||
      isSameDate(date, startDate) ||
      isSameDate(date, endDate)
    );
  };

  const isDateInRange = (date) => {
    if (!date) return false;
    let from = startDate, to = endDate;
    return date >= from && date <= to;
  };

  const isToday = (date) => isSameDate(date, new Date());

  const formatDisplayDate = (date, compact = false) => {
    if (!date) return '';
    const options = compact
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const currentDate = new Date();
  const formattedDate = `Updated ${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getHours()}:${String(currentDate.getMinutes()).padStart(2, '0')} ${currentDate.getHours() >= 12 ? 'PM' : 'AM'}`;

  return (
    <div
      ref={dropdownRef}
      className="mb-3"
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: isMobile ? '12px' : '8px 16px',
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        gap: isMobile ? '12px' : '0',
      }}
    >
      <div style={{ position: 'relative', width: isMobile ? '100%' : 'auto' }}>
        <button
          onClick={toggleDropdown}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: '#4a5568',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            padding: 0,
            fontSize: isMobile ? '15px' : 'inherit',
            fontWeight: 500,
          }}
        >
          {selectedOption} <ChevronDown size={16} />
        </button>

        {isOpen && (
          <div
            style={{
              position: 'absolute',
              zIndex: 20,
              width: isMobile ? '100%' : '192px',
              marginTop: '8px',
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            {options.map((option) => (
              <div
                key={option}
                onClick={() => selectOption(option)}
                className={`option-item ${option === selectedOption ? 'bg-gray-100' : ''}`}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  color: '#4a5568',
                  cursor: 'pointer',
                }}
              >
                {option}
              </div>
            ))}
          </div>
        )}

        {selectedOption === "Custom range" && (
          <div style={{ display: 'flex', flexDirection: 'row', marginTop: '8px', width: isMobile ? '100%' : 'auto' }}>
            <button
              onClick={toggleCalendar}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}
            >
              <Calendar size={16} />
              {startDate && endDate
                ? `${formatDisplayDate(startDate, isMobile)} - ${formatDisplayDate(endDate, isMobile)}`
                : 'Select date range'}
            </button>

            {isCalendarOpen && (
              <CalendarModal
                isMobile={isMobile}
                monthNames={monthNames}
                currentMonth={currentMonth}
                currentYear={currentYear}
                showYearSelector={showYearSelector}
                toggleYearSelector={toggleYearSelector}
                goToPreviousMonth={goToPreviousMonth}
                goToNextMonth={goToNextMonth}
                selectMonth={selectMonth}
                selectYear={selectYear}
                generateYearOptions={generateYearOptions}
                generateCalendarDates={generateCalendarDates}
                handleDateClick={handleDateClick}
                handleDateHover={handleDateHover}
                isSelectedDate={isSelectedDate}
                isDateInRange={isDateInRange}
                isToday={isToday}
                selectingStartDate={selectingStartDate}
                cancelDateSelection={cancelDateSelection}
                applyDateRange={applyDateRange}
                tempStart={tempStart}
              />
            )}
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginLeft: isMobile ? '0' : 'auto',
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'space-between' : 'flex-end',
          marginTop: isMobile ? '4px' : '0',
        }}
      >
        <span style={{ fontSize: '14px', color: '#718096' }}>{formattedDate}</span>
        <button
          onClick={handleRefresh}
          style={{
            padding: '8px',
            color: '#718096',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <RefreshCcw size={isMobile ? 18 : 17} />
        </button>
      </div>
    </div>
  );
}
